import { ElementKind } from '@domain/elements';
import { uuidv4 } from '@studio/utils/id';
import { fromRGBAstring } from '../color.utils';
import { isGroupDataNode, toFlatElementNodeList } from './helpers';
/**
 * These base classes should be used for all data node types
 * eventually
 */
export class BaseDataNode {
    constructor() {
        this.locked = false;
        this.hidden = false;
    }
}
export class BaseDataNodeWithChildren extends BaseDataNode {
    get elements() {
        return Object.freeze(this._elements);
    }
    get nodes() {
        return Object.freeze(this._nodes);
    }
    get _duration() {
        let latestAnimationEnd = 0;
        for (const element of this.elements) {
            if (isGroupDataNode(element)) {
                continue;
            }
            if (element.time + element.duration > latestAnimationEnd) {
                latestAnimationEnd = element.time + element.duration;
            }
        }
        // Rounding workaround due to floating points
        return Math.round(latestAnimationEnd * 100) / 100;
    }
    constructor(input) {
        super();
        this._elements = [];
        this._nodes = [];
        this.id = input.id;
        if (typeof this.id === 'undefined') {
            throw new Error(`No id was set when creating node ${this.name}`);
        }
        this.kind = input.kind;
        if (input.nodes) {
            this.setNodes_m(input.nodes);
        }
        else if (input.elements) {
            this.setNodes_m(input.elements);
        }
    }
    setNodes_m(nodes) {
        this._nodes = nodes.slice();
        this._nodes.forEach(node => this._applyRelationsToNode(node));
        this._setFlatViewElementsList(nodes);
    }
    _setFlatViewElementsList(nodes) {
        this._elements = toFlatElementNodeList(nodes);
        const closestAncestor = this.__parentNode || this.__rootNode;
        closestAncestor === null || closestAncestor === void 0 ? void 0 : closestAncestor._setFlatViewElementsList(closestAncestor.nodes);
    }
    *nodeIterator_m(includeGroups = false, group) {
        if (!group) {
            group = this;
        }
        const nodes = group.nodes;
        switch (group.kind) {
            case "creative" /* CreativeKind.Creative */:
            case ElementKind.Group:
                for (const node of nodes) {
                    switch (node.kind) {
                        case ElementKind.Group:
                            if (includeGroups) {
                                yield node;
                            }
                            for (const n of this.nodeIterator_m(includeGroups, node)) {
                                yield n;
                            }
                            break;
                        default:
                            yield node;
                    }
                }
                break;
            default:
                throw new Error('Unknown group element.');
        }
    }
    _applyRelationsToNode(node) {
        if (isGroupDataNode(this)) {
            node.__rootNode = this.__rootNode;
            node.__parentNode = this;
        }
        else if (this instanceof CreativeDataNode) {
            node.__rootNode = this;
            node.__parentNode = undefined;
        }
    }
    _reverseNodes() {
        const nodeWithChildren = this;
        nodeWithChildren.setNodes_m(this.nodes.slice().reverse());
        for (const node of nodeWithChildren.nodes) {
            if (node instanceof BaseDataNodeWithChildren) {
                node.setNodes_m(node._reverseNodes().nodes);
            }
        }
        return nodeWithChildren;
    }
    addNode_m(node, atIndex) {
        const newNodeList = this.nodes.slice();
        atIndex = atIndex !== null && atIndex !== void 0 ? atIndex : this.nodes.length;
        this._applyRelationsToNode(node);
        newNodeList.splice(atIndex, 0, node);
        this.setNodes_m(newNodeList);
    }
    /**
     * Recursively find a node in the tree
     */
    findNodeById_m(nodeId, includeGroups) {
        for (const node of this.nodeIterator_m(includeGroups)) {
            if (node.id === nodeId) {
                return node;
            }
        }
    }
    removeNodeById_m(nodeId) {
        const newNodeTree = [];
        const nodes = this.nodes;
        for (const node of nodes) {
            if (node.id === nodeId) {
                continue;
            }
            if (isGroupDataNode(node)) {
                node.removeNodeById_m(nodeId);
            }
            newNodeTree.push(node);
        }
        this.setNodes_m(newNodeTree);
    }
}
export class CreativeDataNode extends BaseDataNodeWithChildren {
    get duration() {
        return this._duration;
    }
    constructor(creative) {
        var _a;
        super(Object.assign(Object.assign({}, creative), { kind: "creative" /* CreativeKind.Creative */, nodes: creative.nodes }));
        this.loops = 0;
        this.guidelines = [];
        this.emptyChildrenPreserved = false;
        delete this.hidden;
        delete this.locked;
        delete this.name;
        this.width = creative.width;
        this.height = creative.height;
        this.fill = creative.fill || fromRGBAstring('rgba(255, 255, 255, 1)');
        this.loops = (_a = creative.loops) !== null && _a !== void 0 ? _a : 0;
        this.stopTime = creative.stopTime;
        this.startTime = creative.startTime;
        this.guidelines = creative.guidelines || [];
        this.gifExport = Object.assign({ frames: [], show: false }, creative.gifExport);
        this.preloadImage = Object.assign({ quality: 70, format: 'jpg', frames: [] }, creative.preloadImage);
        this.socialGuide = creative.socialGuide;
    }
    getFirstPreloadImageFrame() {
        if (this.preloadImage.frames.length > 0) {
            return this.preloadImage.frames[0];
        }
        return 1;
    }
    getStopTime_m() {
        if (typeof this.stopTime === 'number') {
            return this.stopTime;
        }
        else {
            return this.duration - 1;
        }
    }
}
export class GroupDataNode extends BaseDataNodeWithChildren {
    get time() {
        const times = new Set();
        for (const node of this.elements) {
            times.add(node.time);
        }
        return Math.min(...times);
    }
    get duration() {
        return this._duration - this.time;
    }
    constructor(input) {
        var _a, _b;
        super(Object.assign(Object.assign({}, input), { kind: ElementKind.Group }));
        this.name = input.name;
        this.locked = (_a = input.locked) !== null && _a !== void 0 ? _a : false;
        this.hidden = (_b = input.hidden) !== null && _b !== void 0 ? _b : false;
        this.__rootNode = input.__rootNode;
        this.__parentNode = input.__parentNode;
        this.cidIndex = input.cidIndex;
        this.parentId = input.parentId;
    }
    /** @@remove STUDIO:START */
    copy(keepId = false) {
        const copy = new GroupDataNode(this);
        copy.id = keepId ? this.id : uuidv4();
        copy.setNodes_m(cloneDeep(this.nodes));
        return copy;
    }
}
